import * as XLSX from "xlsx";

const normalizeText = (value = "") =>
  String(value || "")
    .trim()
    .toLocaleUpperCase("tr-TR")
    .replaceAll("İ", "I")
    .replaceAll("İ", "I")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

const onlyDigits = (value = "") => String(value || "").replace(/\D/g, "");

const getMaskedIdentityParts = (value = "") => {
  const text = String(value || "").trim();
  const parts = text.match(/^(\d{2}).*?(\d{2})$/);

  if (!parts) return null;

  return {
    prefix: parts[1],
    suffix: parts[2]
  };
};

const getIdentityMatchType = (expectedIdentity = "", sgkIdentity = "") => {
  const expectedDigits = onlyDigits(expectedIdentity);
  const sgkDigits = onlyDigits(sgkIdentity);

  if (!expectedIdentity || !sgkDigits) return "none";

  if (expectedDigits.length >= 10 && sgkDigits.length >= 10 && expectedDigits === sgkDigits) {
    return "full";
  }

  const maskedParts = getMaskedIdentityParts(expectedIdentity);

  if (!maskedParts) return "none";

  return sgkDigits.startsWith(maskedParts.prefix) && sgkDigits.endsWith(maskedParts.suffix)
    ? "masked"
    : "none";
};

const identityMatches = (expectedIdentity = "", sgkIdentity = "") => {
  const matchType = getIdentityMatchType(expectedIdentity, sgkIdentity);

  return matchType !== "none";
};

const getExpectedRowKey = ({ identityNo = "", name = "" }) => {
  const identityDigits = onlyDigits(identityNo);
  const normalizedName = normalizeText(name);

  if (identityDigits.length >= 10) {
    return `id:${identityDigits}`;
  }

  if (identityDigits && normalizedName) {
    return `masked-id-name:${identityDigits}:${normalizedName}`;
  }

  return `name:${normalizedName}`;
};

const getNameScore = (expectedName = "", sgkName = "") => {
  const expected = normalizeText(expectedName);
  const actual = normalizeText(sgkName);

  if (!expected || !actual) return 0;
  if (expected === actual) return 1;
  if (expected.includes(actual) || actual.includes(expected)) return 0.9;

  const expectedTokens = new Set(expected.split(" ").filter((token) => token.length > 1));
  const actualTokens = new Set(actual.split(" ").filter((token) => token.length > 1));
  const intersectionCount = Array.from(expectedTokens).filter((token) => actualTokens.has(token)).length;
  const maxTokenCount = Math.max(expectedTokens.size, actualTokens.size, 1);

  return intersectionCount / maxTokenCount;
};

const getPdfLines = async (file) => {
  const [pdfjsLib, pdfjsWorkerModule] = await Promise.all([
    import("pdfjs-dist/legacy/build/pdf"),
    import("pdfjs-dist/legacy/build/pdf.worker.entry")
  ]);
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerModule.default || pdfjsWorkerModule;

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const lines = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const textContent = await page.getTextContent();
    const groupedRows = new Map();

    textContent.items.forEach((item) => {
      const text = String(item.str || "").trim();
      if (!text) return;

      const y = Math.round(item.transform?.[5] || 0);
      const x = item.transform?.[4] || 0;
      const key = String(y);
      const row = groupedRows.get(key) || [];
      row.push({ x, text });
      groupedRows.set(key, row);
    });

    Array.from(groupedRows.entries())
      .sort((a, b) => Number(b[0]) - Number(a[0]))
      .forEach(([, rowItems]) => {
        const line = rowItems
          .sort((a, b) => a.x - b.x)
          .map((item) => item.text)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim();

        if (line) {
          lines.push(line);
        }
      });
  }

  return lines;
};

const parseSgkServiceRows = (lines = []) => {
  const rows = [];

  lines.forEach((line) => {
    const normalizedLine = line.replace(/\s+/g, " ").trim();
    const oldFormatMatch = normalizedLine.match(/^\*?\s*(\d+)\s+(\d{10,11})\s+(.+?)\s+----\s+----\s+(\d{1,2})(?:\s|$)/);
    const serviceListMatch = normalizedLine.match(/^\*?\s*(\d+)\s+(\d{10,11})\s+(.+?)\s+\d{1,3}(?:\.\d{3})*,\d{2}\s+\d{1,3}(?:\.\d{3})*,\d{2}\s+(\d{1,2})(?:\s|$)/);
    const match = oldFormatMatch || serviceListMatch;

    if (!match) return;

    rows.push({
      order: Number(match[1]),
      identityNo: match[2],
      name: match[3].trim(),
      days: Number(match[4]),
      rawLine: normalizedLine
    });
  });

  return rows;
};

const parseSgkMeta = (lines = []) => {
  const text = lines.join("\n");

  return {
    companyName: text.match(/İşyeri Ünvanı\s*:\s*(.+)/)?.[1]?.trim() || "",
    period: text.match(/Yıl\s*-\s*Ay\s*:\s*(\d{4}\/\d{2})/)?.[1] || "",
    law: text.match(/Kanun\s*:\s*([^\n]+)/)?.[1]?.trim() || "",
    approvalDate: text.match(/Onay Tarihi\s*:\s*([^\n]+)/)?.[1]?.trim() || ""
  };
};

const getExpectedRows = (reportRows = []) =>
  Array.from(reportRows.reduce((map, row) => {
    const identityNo = row.tcKimlikNo || row.identityNo || "";
    const name = row.personel || row.name || "";
    const key = getExpectedRowKey({ identityNo, name });
    const existing = map.get(key) || {
      identityNo,
      name,
      expectedDays: 0,
      raw: row
    };

    existing.expectedDays += Number(row.totals?.gun5746 || row.gun5746 || 0);
    map.set(key, existing);

    return map;
  }, new Map()).values())
    .filter((row) => row.name && row.expectedDays > 0)
    .map((row) => ({
      ...row,
      expectedDays: Number.isInteger(row.expectedDays)
        ? row.expectedDays
        : Number(row.expectedDays.toFixed(2))
    }));

const normalizeHeader = (value = "") =>
  normalizeText(value)
    .replaceAll(" ", "")
    .replaceAll("GUN", "GUN")
    .replaceAll("SAYISI", "SAYISI");

export const extractExemptionReportRowsFromWorkbook = (workbook) => {
  if (!workbook?.SheetNames?.length) return [];

  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
    const headerIndex = rows.findIndex((row) => {
      const headers = row.map(normalizeHeader);
      return headers.some((header) => header.includes("PERSONEL")) &&
        headers.some((header) => header.includes("5746") && header.includes("GUN"));
    });

    if (headerIndex < 0) continue;

    const headers = rows[headerIndex].map(normalizeHeader);
    const identityIndex = headers.findIndex((header) =>
      header.includes("TCKIMLIK") || header.includes("KIMLIK") || header === "TC"
    );
    const nameIndex = headers.findIndex((header) => header.includes("PERSONEL") || header.includes("ADISOYADI"));
    const gun5746Index = headers.findIndex((header) => header.includes("5746") && header.includes("GUN"));

    if (nameIndex < 0 || gun5746Index < 0) continue;

    return rows
      .slice(headerIndex + 1)
      .map((row) => ({
        tcKimlikNo: identityIndex >= 0 ? String(row[identityIndex] || "").trim() : "",
        personel: String(row[nameIndex] || "").trim(),
        totals: {
          gun5746: Number(String(row[gun5746Index] || "0").replace(",", ".")) || 0
        }
      }))
      .filter((row) => row.personel && Number(row.totals.gun5746 || 0) > 0);
  }

  return [];
};

const matchRows = (expectedRows = [], sgkRows = []) => {
  const usedSgkIndexes = new Set();
  const comparisonRows = expectedRows.map((expectedRow) => {
    const candidates = sgkRows
      .map((sgkRow, index) => {
        if (usedSgkIndexes.has(index)) return null;

        const identityMatchType = getIdentityMatchType(expectedRow.identityNo, sgkRow.identityNo);
        const idMatch = identityMatches(expectedRow.identityNo, sgkRow.identityNo);
        const fullIdMatch = identityMatchType === "full";
        const maskedIdMatch = identityMatchType === "masked";
        const nameScore = getNameScore(expectedRow.name, sgkRow.name);
        const score = fullIdMatch
          ? 0.82 + (nameScore * 0.18)
          : maskedIdMatch
            ? (nameScore >= 0.55 ? 0.45 + (nameScore * 0.55) : 0)
            : nameScore;

        return {
          sgkRow,
          index,
          idMatch,
          fullIdMatch,
          maskedIdMatch,
          nameScore,
          score
        };
      })
      .filter(Boolean)
      .filter((candidate) =>
        candidate.fullIdMatch ||
        candidate.nameScore >= 0.72 ||
        (candidate.maskedIdMatch && candidate.nameScore >= 0.55)
      )
      .sort((a, b) => b.score - a.score);

    const bestCandidate = candidates[0] || null;

    if (!bestCandidate) {
      return {
        expectedName: expectedRow.name,
        sgkName: "",
        expectedIdentityNo: expectedRow.identityNo,
        sgkIdentityNo: "",
        expectedDays: expectedRow.expectedDays,
        sgkDays: null,
        difference: expectedRow.expectedDays,
        matchScore: 0,
        status: "missing",
        statusLabel: "SGK’da Yok"
      };
    }

    usedSgkIndexes.add(bestCandidate.index);

    const difference = Number(bestCandidate.sgkRow.days || 0) - Number(expectedRow.expectedDays || 0);
    const isDayMatch = difference === 0;
    const isStrongMatch =
      bestCandidate.fullIdMatch ||
      bestCandidate.nameScore >= 0.86 ||
      (bestCandidate.maskedIdMatch && bestCandidate.nameScore >= 0.72);

    return {
      expectedName: expectedRow.name,
      sgkName: bestCandidate.sgkRow.name,
      expectedIdentityNo: expectedRow.identityNo,
      sgkIdentityNo: bestCandidate.sgkRow.identityNo,
      expectedDays: expectedRow.expectedDays,
      sgkDays: bestCandidate.sgkRow.days,
      difference,
      matchScore: Math.round(bestCandidate.score * 100),
      status: !isStrongMatch ? "uncertain" : isDayMatch ? "matched" : "dayMismatch",
      statusLabel: !isStrongMatch ? "Eşleşme Kontrolü" : isDayMatch ? "Uyumlu" : "Gün Farkı"
    };
  });

  sgkRows.forEach((sgkRow, index) => {
    if (usedSgkIndexes.has(index)) return;

    comparisonRows.push({
      expectedName: "",
      sgkName: sgkRow.name,
      expectedIdentityNo: "",
      sgkIdentityNo: sgkRow.identityNo,
      expectedDays: null,
      sgkDays: sgkRow.days,
      difference: sgkRow.days,
      matchScore: 0,
      status: "extra",
      statusLabel: "Bizim Raporda Yok"
    });
  });

  return comparisonRows;
};

export const compareExemptionReportWithSgkPdf = async ({ file, reportRows = [] }) => {
  if (!file) {
    throw new Error("SGK hizmet listesi PDF’i seçilmedi.");
  }

  if (!Array.isArray(reportRows) || reportRows.length === 0) {
    throw new Error("Karşılaştırma için önce muafiyetli gün raporu hesaplanmalı.");
  }

  const lines = await getPdfLines(file);
  const sgkRows = parseSgkServiceRows(lines);

  if (sgkRows.length === 0) {
    throw new Error("SGK PDF’inde okunabilir hizmet listesi satırı bulunamadı.");
  }

  const expectedRows = getExpectedRows(reportRows);
  const rows = matchRows(expectedRows, sgkRows);
  const summary = rows.reduce((acc, row) => {
    acc.total += 1;
    acc.expectedTotal += Number(row.expectedDays || 0);
    acc.sgkTotal += Number(row.sgkDays || 0);

    if (row.status === "matched") acc.matched += 1;
    if (row.status === "dayMismatch") acc.dayMismatch += 1;
    if (row.status === "missing") acc.missing += 1;
    if (row.status === "extra") acc.extra += 1;
    if (row.status === "uncertain") acc.uncertain += 1;

    return acc;
  }, {
    total: 0,
    matched: 0,
    dayMismatch: 0,
    missing: 0,
    extra: 0,
    uncertain: 0,
    expectedTotal: 0,
    sgkTotal: 0
  });

  return {
    meta: parseSgkMeta(lines),
    expectedCount: expectedRows.length,
    sgkCount: sgkRows.length,
    rows,
    summary,
    sourceFileName: file.name || ""
  };
};
